import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Layout from './components/Layout';
import Dashboard from './pages/Dashboard';
import HealthCheck from './pages/HealthCheck';
import TldList from './pages/TldList';
import BulkTldCheck from './pages/BulkTldCheck';
import ApiTokens from './pages/ApiTokens';
import ApiTesting from './pages/ApiTesting';
import UserManagement from './pages/UserManagement';
import Help from './pages/Help';
import Login from './pages/Login';
import ApiService from './services/api';

// Protected route component
const ProtectedRoute: React.FC<{
  children: React.ReactNode;
  redirectTo?: string;
}> = ({ children, redirectTo = '/login' }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        // Check if we have a token first
        const token = localStorage.getItem('auth_token');
        if (!token) {
          setIsAuthenticated(false);
          setIsLoading(false);
          return;
        }

        // Try to get current user info - this requires authentication
        await ApiService.getCurrentUser();
        setIsAuthenticated(true);
      } catch (error) {
        console.log('Authentication check failed:', error);
        // Clear invalid token
        localStorage.removeItem('auth_token');
        setIsAuthenticated(false);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, []);

  if (isLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        backgroundColor: '#f8f9fa'
      }}>
        <div style={{
          width: '40px',
          height: '40px',
          border: '4px solid #e9ecef',
          borderTop: '4px solid #4263eb',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }} />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to={redirectTo} replace />;
  }

  return <>{children}</>;
};

// Admin-only route component
const AdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<'loading' | 'admin' | 'denied' | 'unauth'>('loading');

  useEffect(() => {
    const check = async () => {
      try {
        const token = localStorage.getItem('auth_token');
        if (!token) { setState('unauth'); return; }
        const user = await ApiService.getCurrentUser();
        setState(user.role === 'admin' ? 'admin' : 'denied');
      } catch {
        localStorage.removeItem('auth_token');
        setState('unauth');
      }
    };
    check();
  }, []);

  if (state === 'loading') return null;
  if (state === 'unauth') return <Navigate to="/login" replace />;
  if (state === 'denied') return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
};

const App: React.FC = () => {
  const [currentPage, setCurrentPage] = useState('dashboard');

  const handlePageChange = (page: string) => {
    setCurrentPage(page);
  };

  return (
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route 
          path="/" 
          element={
            <ProtectedRoute>
              <Layout currentPage={currentPage} onPageChange={handlePageChange}>
                <Dashboard />
              </Layout>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/dashboard" 
          element={
            <ProtectedRoute>
              <Layout currentPage="dashboard" onPageChange={handlePageChange}>
                <Dashboard />
              </Layout>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/health" 
          element={
            <ProtectedRoute>
              <Layout currentPage="health" onPageChange={handlePageChange}>
                <HealthCheck />
              </Layout>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/tlds" 
          element={
            <ProtectedRoute>
              <Layout currentPage="tlds" onPageChange={handlePageChange}>
                <TldList />
              </Layout>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/bulk-check" 
          element={
            <AdminRoute>
              <Layout currentPage="bulk-check" onPageChange={handlePageChange}>
                <BulkTldCheck />
              </Layout>
            </AdminRoute>
          } 
        />
        <Route 
          path="/tokens" 
          element={
            <AdminRoute>
              <Layout currentPage="tokens" onPageChange={handlePageChange}>
                <ApiTokens />
              </Layout>
            </AdminRoute>
          } 
        />
        <Route 
          path="/testing" 
          element={
            <ProtectedRoute>
              <Layout currentPage="testing" onPageChange={handlePageChange}>
                <ApiTesting />
              </Layout>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/users" 
          element={
            <AdminRoute>
              <Layout currentPage="users" onPageChange={handlePageChange}>
                <UserManagement />
              </Layout>
            </AdminRoute>
          } 
        />
        <Route 
          path="/help" 
          element={
            <ProtectedRoute>
              <Layout currentPage="help" onPageChange={handlePageChange}>
                <Help />
              </Layout>
            </ProtectedRoute>
          } 
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
  );
};

export default App; 